import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { sanitizeHtml } from "../artifactBodyUtils";
import { buildArtifactKickerMeta } from "./blockRendererUtils";
import { V44Icon } from "./v44Icons";
import "./rothermel-v44.css";

const sortByOrder = items =>
  [...(items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const html = value => ({ __html: sanitizeHtml(value) });

const scrollElementTo = (element, { left, top, behavior = "auto" } = {}) => {
  if (typeof element?.scrollTo === "function") {
    element.scrollTo({ left, top, behavior });
    return;
  }
  if (left !== undefined) {
    element.scrollLeft = left;
  }
  if (top !== undefined) {
    element.scrollTop = top;
  }
};

function BlockWrap({ block, children, className = "" }) {
  return (
    <div
      className={`block ${className}`.trim()}
      data-id={block.id}
      data-vis={block.visibility}
    >
      {children}
    </div>
  );
}

BlockWrap.propTypes = {
  block: PropTypes.object.isRequired,
  children: PropTypes.node,
  className: PropTypes.string
};

function renderChildren(blocks) {
  return sortByOrder(blocks).map(block => (
    <BlockNode key={block.id} block={block} />
  ));
}

function ScoreDots({ score, color = "var(--accent)" }) {
  return (
    <div className="opp-dots">
      <div className="dots">
        {Array.from({ length: 5 }, (_, index) => (
          <span
            key={index}
            className="dot"
            style={{
              background: color,
              opacity: index < Number(score || 0) ? 1 : 0.18
            }}
          />
        ))}
      </div>
      <span className="sc">{score}/5</span>
    </div>
  );
}

ScoreDots.propTypes = {
  score: PropTypes.number,
  color: PropTypes.string
};

function BlockNode({ block }) {
  const { type, content = {}, children = [] } = block;
  const sortedChildren = sortByOrder(children);

  switch (type) {
    case "section":
      return null;

    case "prose":
      return (
        <BlockWrap block={block}>
          <p
            className={`prose${content.cls ? ` ${content.cls}` : ""}`}
            dangerouslySetInnerHTML={html(content.html)}
          />
        </BlockWrap>
      );

    case "lead":
      return (
        <BlockWrap block={block}>
          <p className="lead" dangerouslySetInnerHTML={html(content.html)} />
        </BlockWrap>
      );

    case "contacts":
      return (
        <BlockWrap block={block}>
          <div className="contacts">{renderChildren(sortedChildren)}</div>
        </BlockWrap>
      );

    case "contactcard":
      return (
        <BlockWrap block={block}>
          <div className="contact">{renderChildren(sortedChildren)}</div>
        </BlockWrap>
      );

    case "contacthead":
      return (
        <BlockWrap block={block}>
          <div className="ch-top">
            <div
              className="ava"
              style={{ background: content.bg, color: content.color }}
            >
              {content.initials}
            </div>
            <div className="ch-info">
              <div className="nm">{content.name}</div>
              {content.role && <div className="ro">{content.role}</div>}
              {content.co && <div className="co">{content.co}</div>}
            </div>
          </div>
        </BlockWrap>
      );

    case "contactnote":
      return (
        <BlockWrap block={block}>
          <div className="cn">{content.text}</div>
        </BlockWrap>
      );

    case "profilgrid":
      return (
        <BlockWrap block={block}>
          <div className="profil-grid">
            {sortByOrder(content.items).map(item => (
              <BlockWrap
                key={item.id}
                block={{ id: item.id, visibility: item.visibility }}
              >
                <div className="profil-row">
                  <div className="k">{item.k}</div>
                  <div className="v">{item.v}</div>
                </div>
              </BlockWrap>
            ))}
          </div>
        </BlockWrap>
      );

    case "swimlane":
      return (
        <BlockWrap block={block}>
          <div className="swim">
            {sortByOrder(content.phases).map((phase, index, phases) => (
              <React.Fragment key={`${block.id}-phase-${index}`}>
                <div className={`sw-lane${phase.eng ? " eng" : ""}`}>
                  {phase.eng ? (
                    <div className="sw-lh">
                      {phase.day}
                      {phase.pill && <span className="sw-pill">{phase.pill}</span>}
                    </div>
                  ) : (
                    <>
                      <div className="sw-lh">{phase.day}</div>
                      {phase.role && <div className="sw-lsub">{phase.role}</div>}
                    </>
                  )}
                  {sortByOrder(phase.steps).map((step, stepIndex) => (
                    <div key={`${block.id}-step-${stepIndex}`} className="sw-step">
                      <div className="sw-circ">
                        <V44Icon name={step.icon} />
                      </div>
                      <div>
                        <div className="sw-t">{step.t}</div>
                        {step.s && <div className="sw-s">{step.s}</div>}
                      </div>
                    </div>
                  ))}
                  {phase.stats?.length > 0 && (
                    <div className="sw-stat">
                      {phase.stats.map((stat, statIndex) => (
                        <div key={`${block.id}-stat-${statIndex}`} className="sw-blk">
                          <div className="sw-n">{stat.n}</div>
                          <div className="sw-l">{stat.l}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {index < phases.length - 1 && (
                  <div className="sw-chev" aria-hidden="true">
                    ›
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </BlockWrap>
      );

    case "list":
      return (
        <BlockWrap block={block}>
          {content.title && <div className="grp-title">{content.title}</div>}
          <ul className="clean">
            {(content.items || []).map((item, index) => (
              <li key={`${block.id}-li-${index}`}>
                <span className="mk" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </BlockWrap>
      );

    case "asset":
      return (
        <BlockWrap block={block}>
          <div className="asset">
            <V44Icon name="check" />
            <p>
              <span className="alabel">{content.label}</span>{" "}
              <span dangerouslySetInnerHTML={html(content.html)} />
            </p>
          </div>
        </BlockWrap>
      );

    case "callout":
      return (
        <BlockWrap block={block}>
          <div className={`callout${content.cls ? ` ${content.cls}` : ""}`}>
            <p dangerouslySetInnerHTML={html(content.html)} />
          </div>
        </BlockWrap>
      );

    case "cards":
      return (
        <BlockWrap block={block}>
          {content.title && <div className="grp-title">{content.title}</div>}
          <div className="grid">
            {sortByOrder(content.cards).map(card => (
              <BlockWrap
                key={card.id}
                block={{ ...block, id: card.id, visibility: card.visibility }}
              >
                <div className="card">
                  {card.lbl && (
                    <span className={`lbl ${card.lbl.c}`}>{card.lbl.t}</span>
                  )}
                  {card.h && <h3>{card.h}</h3>}
                  {card.p && <p dangerouslySetInnerHTML={html(card.p)} />}
                </div>
              </BlockWrap>
            ))}
          </div>
        </BlockWrap>
      );

    case "card-solo":
      return (
        <BlockWrap block={block}>
          <div className="card">
            {content.h && <h3>{content.h}</h3>}
            {content.p && <p dangerouslySetInnerHTML={html(content.p)} />}
            {content.more && (
              <BlockWrap
                block={{
                  ...block,
                  id: content.more.id,
                  visibility: content.more.visibility
                }}
              >
                <details className="more">
                  <summary>
                    <span className="chev">›</span>
                    {content.more.label}
                  </summary>
                  <div
                    className="body"
                    dangerouslySetInnerHTML={html(content.more.body)}
                  />
                </details>
              </BlockWrap>
            )}
          </div>
        </BlockWrap>
      );

    case "painlist":
      return (
        <BlockWrap block={block}>
          <div className="painlist">
            {sortByOrder(content.items).map(item => (
              <BlockWrap
                key={item.id}
                block={{ ...block, id: item.id, visibility: item.visibility }}
              >
                <div className="pain-item">
                  <span className="pi-ic">
                    <V44Icon name={item.icon || "warn"} />
                  </span>
                  <span
                    className="pi-tx"
                    dangerouslySetInnerHTML={html(item.html)}
                  />
                </div>
              </BlockWrap>
            ))}
          </div>
        </BlockWrap>
      );

    case "trow": {
      const badge = sortedChildren.find(child => child.type === "tbadge");
      const rest = sortedChildren.filter(child => child.type !== "tbadge");
      return (
        <BlockWrap block={block}>
          <div className="trow">
            <div className="ti" style={{ color: content.iconColor || "var(--accent)" }}>
              <V44Icon name={content.icon || "db"} />
            </div>
            <div className="tt">
              <div className="tn">{content.name}</div>
              {content.desc && <div className="td">{content.desc}</div>}
            </div>
            {badge && (
              <BlockWrap block={badge}>
                <span
                  className="tbadge"
                  style={{ background: badge.content.bg, color: badge.content.color }}
                >
                  {badge.content.text}
                </span>
              </BlockWrap>
            )}
          </div>
          {renderChildren(rest)}
        </BlockWrap>
      );
    }

    case "tbadge":
      return null;

    case "opp": {
      const scoreBlock = sortedChildren.find(child => child.type === "score");
      const bodyBlocks = sortedChildren.filter(
        child => child.type !== "score"
      );
      return (
        <BlockWrap block={block}>
          <div className="opp">
            <div className="opp-head">
              <div
                className="opp-num"
                style={{
                  background: content.numBg,
                  color: content.numColor
                }}
              >
                {content.num}
              </div>
              <div className="opp-tt">
                <div className="opp-title">{content.title}</div>
              </div>
              {scoreBlock && (
                <ScoreDots
                  score={scoreBlock.content.score}
                  color={scoreBlock.content.color}
                />
              )}
            </div>
            {bodyBlocks.length > 0 && (
              <div className="opp-body">{renderChildren(bodyBlocks)}</div>
            )}
          </div>
        </BlockWrap>
      );
    }

    case "score":
      return null;

    case "opptype":
      return (
        <BlockWrap block={block}>
          <div className="opp-type">{content.text}</div>
        </BlockWrap>
      );

    case "metagrid":
      return (
        <BlockWrap block={block}>
          <div className="metagrid">{renderChildren(sortedChildren)}</div>
        </BlockWrap>
      );

    case "pill":
      return (
        <BlockWrap block={block}>
          <div className="metapill">
            <div className="k">{content.k}</div>
            <div className="v">{content.v}</div>
          </div>
        </BlockWrap>
      );

    case "steps2":
      return (
        <BlockWrap block={block}>
          <div className="steps2">
            {sortByOrder(content.items).map(item => (
              <BlockWrap
                key={item.id}
                block={{ ...block, id: item.id, visibility: item.visibility }}
              >
                <div className="step2">
                  <div className="s2-rail">
                    <div className="s2-n">{item.n}</div>
                  </div>
                  <div className="s2-b">
                    <div className="s2-head">
                      <div className="s2-t">{item.title}</div>
                      {(item.who || item.when) && (
                        <div className="s2-meta">
                          {item.who && (
                            <span className="s2-tag s2-who">{item.who}</span>
                          )}
                          {item.when && (
                            <span className="s2-tag s2-when">{item.when}</span>
                          )}
                        </div>
                      )}
                    </div>
                    {item.desc && <div className="s2-d">{item.desc}</div>}
                  </div>
                </div>
              </BlockWrap>
            ))}
          </div>
        </BlockWrap>
      );

    default:
      return null;
  }
}

BlockNode.propTypes = {
  block: PropTypes.object.isRequired
};

function SectionNode({ block, sectionNumber }) {
  const title = block.content?.title || "";
  return (
    <section id={block.id}>
      <BlockWrap block={block}>
        <div className="sec-bar">
          {sectionNumber && <div className="sec-num">{sectionNumber}</div>}
          <h2>{title}</h2>
        </div>
        <div className="blocks">{renderChildren(block.children)}</div>
      </BlockWrap>
    </section>
  );
}

SectionNode.propTypes = {
  block: PropTypes.object.isRequired,
  sectionNumber: PropTypes.string
};

function KickerMeta({ meta }) {
  if (!meta) {
    return null;
  }

  return (
    <>
      <span className="kr">{meta.typeLabel}</span>
      <span className="kx">
        <V44Icon name="users" className="picon" width={12} height={12} />
        {meta.participants}
      </span>
      {meta.location && (
        <span className="kmeta">
          <V44Icon name="map-pin" className="ticon" />
          {meta.location}
        </span>
      )}
      {meta.date && (
        <span className="kmeta">
          <V44Icon name="clock" className="ticon" width={12} height={12} />
          {meta.date}
        </span>
      )}
    </>
  );
}

KickerMeta.propTypes = {
  meta: PropTypes.object
};

function ArtifactHero({ meta }) {
  if (!meta?.title) {
    return null;
  }

  return (
    <header className="hero" id="top">
      <div className="kicker-m">
        <KickerMeta meta={meta} />
      </div>
      <h1>{meta.title}</h1>
      {meta.lead && <p className="dek">{meta.lead}</p>}
    </header>
  );
}

ArtifactHero.propTypes = {
  meta: PropTypes.object
};

export function BlockRenderer({
  blocks,
  artifact,
  workspace,
  isInternalUser = false,
  backLabel,
  onBack
}) {
  const sections = useMemo(() => sortByOrder(blocks || []), [blocks]);
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef(null);

  const kickerMeta = useMemo(
    () =>
      artifact
        ? buildArtifactKickerMeta({ artifact, workspace, blocks: sections })
        : null,
    [artifact, workspace, sections]
  );

  const getStickyOffset = () => {
    const root = rootRef.current;
    if (!root) {
      return 0;
    }

    const isVisible = element =>
      Boolean(element) && window.getComputedStyle(element).display !== "none";

    const pageTitle = root.querySelector(".pagetitle");
    const tocStrip = root.querySelector(".tocstrip");

    let offset = 0;
    if (isVisible(pageTitle)) {
      offset += pageTitle.offsetHeight;
    }
    if (isVisible(tocStrip)) {
      offset += tocStrip.offsetHeight;
    }
    return offset;
  };

  useEffect(() => {
    const scrollContainer = rootRef.current?.closest(
      "[data-collaboration-scroll]"
    );
    if (!scrollContainer || !sections.length) {
      return undefined;
    }

    const syncActiveSection = () => {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const activationOffset = getStickyOffset() + 12;
      let currentId = sections[0].id;

      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (!element) {
          return;
        }

        const sectionTop = element.getBoundingClientRect().top - containerTop;
        if (sectionTop <= activationOffset) {
          currentId = section.id;
        }
      });

      setActiveId(currentId);

      const heroTitle = rootRef.current?.querySelector(".hero h1");
      if (!heroTitle) {
        setScrolled(false);
      } else {
        const heroBottom = heroTitle.getBoundingClientRect().bottom - containerTop;
        setScrolled(heroBottom < 8);
      }
    };

    scrollContainer.addEventListener("scroll", syncActiveSection, {
      passive: true
    });
    syncActiveSection();

    return () => scrollContainer.removeEventListener("scroll", syncActiveSection);
  }, [sections]);

  useEffect(() => {
    if (!scrolled) {
      return;
    }

    const strip = rootRef.current?.querySelector(".tocstrip");
    if (!strip || window.getComputedStyle(strip).display === "none") {
      return;
    }

    const activeLink = strip.querySelector("a.active");
    if (!activeLink) {
      return;
    }

    const list = strip.querySelector("ul");
    if (!list) {
      return;
    }

    const targetLeft =
      activeLink.offsetLeft - (list.clientWidth - activeLink.clientWidth) / 2;
    scrollElementTo(list, {
      left: Math.max(0, targetLeft),
      behavior: "smooth"
    });
  }, [activeId, scrolled]);

  const scrollToSection = sectionId => {
    const scrollContainer = rootRef.current?.closest(
      "[data-collaboration-scroll]"
    );
    const element = document.getElementById(sectionId);
    if (!scrollContainer || !element) {
      return;
    }

    const containerTop = scrollContainer.getBoundingClientRect().top;
    const sectionTop = element.getBoundingClientRect().top;
    const stickyOffset = getStickyOffset() + 8;
    scrollElementTo(scrollContainer, {
      top:
        scrollContainer.scrollTop + sectionTop - containerTop - stickyOffset,
      behavior: "smooth"
    });
    setActiveId(sectionId);
  };

  if (!sections.length) {
    return null;
  }

  const rootClass = [
    "rothermel-v44",
    "embedded",
    isInternalUser ? "internal" : "external-view",
    scrolled ? "scrolled" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const navSections = sections.map((section, index) => ({
    section,
    num: String(index + 1).padStart(2, "0")
  }));

  return (
    <div className={rootClass} ref={rootRef}>
      <div className="wrap">
        <nav className="toc">
          {onBack && (
            <button
              type="button"
              className="toc-back"
              onClick={onBack}
              aria-label={backLabel ? `Zurück zu ${backLabel}` : "Zurück"}
              title={backLabel ? `Zurück zu ${backLabel}` : "Zurück"}
            >
              <V44Icon name="chevron-left" width={14} height={14} />
              <span>Zurück</span>
            </button>
          )}
          <div className="eyebrow">
            <KickerMeta meta={kickerMeta} />
          </div>
          <ul>
            {navSections.map(({ section, num }) => {
              return (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={activeId === section.id ? "active" : ""}
                    onClick={event => {
                      event.preventDefault();
                      scrollToSection(section.id);
                    }}
                  >
                    <span className="num">{num}</span>
                    {section.content?.title}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        <main>
          <ArtifactHero meta={kickerMeta} />
          {kickerMeta?.title && (
            <div className="pagetitle">{kickerMeta.title}</div>
          )}
          <nav className="tocstrip" aria-label="Abschnitte">
            <ul>
              {navSections.map(({ section, num }) => (
                <li key={`strip-${section.id}`}>
                  <a
                    href={`#${section.id}`}
                    className={scrolled && activeId === section.id ? "active" : ""}
                    onClick={event => {
                      event.preventDefault();
                      scrollToSection(section.id);
                    }}
                  >
                    <span className="num">{num}</span>
                    {section.content?.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div id="content">
            {navSections.map(({ section, num }) => (
              <SectionNode
                key={section.id}
                block={section}
                sectionNumber={num}
              />
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}

BlockRenderer.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  artifact: PropTypes.object,
  workspace: PropTypes.object,
  isInternalUser: PropTypes.bool,
  backLabel: PropTypes.string,
  onBack: PropTypes.func
};

export default BlockRenderer;
